import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import {
  blockingOpen, gatesByGroup, gateAttribution, GATE_GROUP_LABEL, GATE_STATUS_META,
  type BetaGate, type GateStatus,
} from '@/lib/betaConsole';
import { LoadingNote, ErrorNote } from '@/admin';

const STATUS_OPTIONS: GateStatus[] = ['not_started', 'in_progress', 'blocked', 'done', 'deferred'];

/** Preflight gates. Modelled on the registry preflight: bad state fails loud and
 *  refuses service rather than degrading quietly. A gate marked done is a claim
 *  made on the record — the write is audited server-side, not just here. */
export function ReadinessTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['beta-gates'], queryFn: () => adminApi.beta.gates.list() });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status?: GateStatus; note?: string } }) =>
      adminApi.beta.gates.patch(id, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['beta-gates'] }); },
  });

  if (q.isLoading) return <LoadingNote />;
  if (q.isError) {
    return (
      <ErrorNote>
        Could not load preflight gates. The <code>/admin/beta/gates</code> endpoint is owned by
        beta-console-backend and may not be deployed here yet.
      </ErrorNote>
    );
  }

  const gates = q.data?.gates ?? [];
  const open = blockingOpen(gates);
  const done = gates.filter((g) => g.status === 'done').length;
  const pct = gates.length ? Math.round((done / gates.length) * 100) : 0;

  return (
    <>
      <section className="mb-13 rounded border border-border bg-surface p-13">
        <h2 className="mb-2 text-body font-semibold text-text-primary">Preflight</h2>
        <div
          className={`rounded border-l-2 p-13 ${open.length ? 'border-flagged bg-surface-sunken' : 'border-accent bg-surface-sunken'}`}
        >
          <p className="text-body-sm text-text-primary">
            {open.length ? (
              <>
                <strong>{open.length} blocking gate{open.length > 1 ? 's' : ''} open.</strong> Not
                cleared to onboard. Screening and consent may proceed; nobody sees a report until
                this clears.
              </>
            ) : (
              <><strong>All blocking gates clear.</strong> Cleared to onboard.</>
            )}
          </p>
        </div>
        <p className="mt-2 text-body-sm text-text-muted">
          A gate marked done is a claim you are making on the record.
        </p>
        <div className="mt-4">
          <div className="h-[3px] w-full overflow-hidden rounded bg-surface-sunken">
            <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-text-muted">{done}/{gates.length} gates done</div>
        </div>
      </section>

      {gatesByGroup(gates).map(({ group, rows }) => (
        <section key={group} className="mb-13 rounded border border-border bg-surface p-13">
          <h2 className="mb-2 text-body font-semibold text-text-primary">{GATE_GROUP_LABEL[group]}</h2>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-1/4">Gate</th>
                  <th>Why</th>
                  <th className="w-16">Owner</th>
                  <th className="w-32">Status</th>
                  <th className="w-1/6">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => <GateRow key={g.id} gate={g} canEdit={canEdit} onPatch={patch.mutate} />)}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}

function GateRow({ gate, canEdit, onPatch }: {
  gate: BetaGate;
  canEdit: boolean;
  onPatch: (v: { id: string; body: { status?: GateStatus; note?: string } }) => void;
}) {
  const meta = GATE_STATUS_META[gate.status];
  return (
    <tr>
      <td className="align-top">
        <strong className="text-text-primary">{gate.label}</strong>{' '}
        <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${gate.blocking ? 'mod-json' : 'mod-calc'}`}>
          {gate.blocking ? 'blocking' : 'deferred'}
        </span>
      </td>
      <td className="align-top text-text-secondary">{gate.detail}</td>
      <td className="align-top font-mono text-[12px]">{gate.owner}</td>
      <td className="align-top">
        {canEdit ? (
          <select
            className="searchbox !mb-0 !w-auto"
            aria-label={`Status — ${gate.label}`}
            value={gate.status}
            onChange={(e) => onPatch({ id: gate.id, body: { status: e.target.value as GateStatus } })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{GATE_STATUS_META[s].label}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
            {meta.label}
          </span>
        )}
      </td>
      <td className="align-top">
        {canEdit ? (
          <input
            className="searchbox !mb-0"
            aria-label={`Note — ${gate.label}`}
            defaultValue={gate.note}
            placeholder="—"
            onBlur={(e) => {
              if (e.target.value !== gate.note) onPatch({ id: gate.id, body: { note: e.target.value } });
            }}
          />
        ) : (
          <span className="text-text-secondary">{gate.note || '—'}</span>
        )}
        {/* Who made the claim. Absent means the gate still sits at its seeded
            default — nobody has asserted anything about it yet. */}
        <div className="mt-1 font-mono text-[10px] text-text-muted">
          {gateAttribution(gate) ?? 'unclaimed'}
        </div>
      </td>
    </tr>
  );
}
